import { Fragment, useRef } from 'react';
import { useIntl } from 'react-intl';
import { InsightsChart, Tag } from 'sonata-react-components';
import { Formatters } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';

import { QuickReportTooltipPortal } from './QuickReportTooltip';
import { getYAxisFloor } from './quickReportChart.helper';

import type { QuickReportTooltipPortalHandle, QuickReportTooltipProps } from './QuickReportTooltip';
import type { TooltipPoint } from './quickReportChart.helper';

import './quickReportChart.scss';

type SummaryItem = {
  amount: string;
  percentage?: string;
  subtitle: string;
};

type Props = {
  chart: {
    categories: string[];
    data?: any[];
    formatValue: (value: number) => string;
    goalLabel: string;
    goalType: string;
    height: number;
    icon: string;
    isEmpty: boolean;
    key: string;
    legend: string;
    seriesName: string;
    summary: SummaryItem[];
    target: number;
    title: string;
    tooltipPoints: TooltipPoint[];
  };
  id: string;
};

// Highcharts draws SVG attributes, which can't read CSS custom properties
const ACTIVE_POINT_COLOR = '#3D5787';
const HOVER_GUIDE_COLOR = '#7B9FE1';

type HoveredPoint = {
  index: number;
  plotX: number;
  plotY: number;
  series: { chart: { container: HTMLElement; plotLeft: number; plotTop: number } };
};

export function QuickReportChart({ chart, id }: Props) {
  const { formatMessage } = useIntl();
  const isBudget = chart.goalType === 'budget';
  const goalLabel = chart.goalLabel.toLowerCase();
  const tooltipRef = useRef<QuickReportTooltipPortalHandle>(null);

  const getTooltipProps = (point: TooltipPoint): QuickReportTooltipProps => {
    return {
      date: point.day.format('ddd, D MMM YYYY'),
      ...(point.dayNumber && {
        dayLabel: formatMessage(
          { defaultMessage: 'Day {day} of {total}', id: 'common.quickReport.tooltip.day' },
          { day: point.dayNumber, total: point.totalDays }
        )
      }),
      progress: {
        label: formatMessage(
          isBudget
            ? { defaultMessage: '{pct} of {target} budget goal', id: 'common.quickReport.tooltip.budget.goalProgress' }
            : { defaultMessage: '{pct} of {target} {goal} goal', id: 'common.quickReport.tooltip.goalProgress' },
          {
            goal: goalLabel,
            pct: Formatters.percentage(point.progressPct, { base: 100, fixedDecimals: 1 }),
            target: chart.formatValue(chart.target)
          }
        ),
        pct: point.progressPct
      },
      rows: [
        {
          label: formatMessage(
            isBudget
              ? { defaultMessage: 'Spent this day', id: 'common.quickReport.tooltip.budget.dayValue' }
              : { defaultMessage: '{goal} this day', id: 'common.quickReport.tooltip.dayValue' },
            { goal: chart.goalLabel }
          ),
          value: chart.formatValue(point.dayValue)
        },
        {
          label: formatMessage(
            isBudget
              ? { defaultMessage: 'Total spent', id: 'common.quickReport.tooltip.budget.total' }
              : { defaultMessage: 'Total {goal}', id: 'common.quickReport.tooltip.total' },
            { goal: goalLabel }
          ),
          value: chart.formatValue(point.cumulative)
        }
      ]
    };
  };

  return (
    <div className="quickReportChart" id={`${id}_quickReportTop_chart`}>
      <div className="quickReportChart__header">
        <i className={`quickReportChart__headerIcon ${chart.icon}`} />
        <span className="quickReportChart__headerTitle">{chart.title}</span>
      </div>
      <div className="quickReportChart__body">
        <div className="quickReportChart__summary">
          {chart.summary.map(({ amount, percentage, subtitle }, index) => (
            <Fragment key={subtitle}>
              <div className="quickReportChart__summarySection">
                <div className="quickReportChart__summarySectionText">
                  <span className="quickReportChart__summarySectionTextAmount h4">{amount}</span>
                  <span className="quickReportChart__summarySectionTextSubtitle h6">{subtitle}</span>
                </div>
                {percentage ? (
                  <div className="quickReportChart__summarySectionPercentage">
                    <Tag id={`${id}_quickReportChart_${subtitle}_tag`}>{percentage}</Tag>
                  </div>
                ) : null}
              </div>
              {index < chart.summary.length - 1 ? <div className="quickReportChart__summaryDivider" /> : null}
            </Fragment>
          ))}
        </div>
        <div className="quickReportChart__graph">
          <InsightsChart
            chartConfig={{
              areaConfig: {
                fillColor: 'rgba(194, 211, 241, 0.8)',
                fillOpacity: 1,
                lineColor: ACTIVE_POINT_COLOR,
                lineWidth: 2,
                marker: {
                  enabled: false,
                  states: {
                    hover: {
                      enabled: true,
                      fillColor: ACTIVE_POINT_COLOR,
                      lineColor: '#FFFFFF',
                      lineWidth: 2,
                      radius: 5
                    }
                  }
                },
                states: { hover: { halo: { opacity: 0.25, size: 10 } } },
                // Fill down to the axis floor so the band below zero is part of the area
                threshold: null
              },
              height: chart.height,
              legend: false,
              lineOptions: {
                lineColor: ACTIVE_POINT_COLOR,
                lineWidth: 2,
                point: {
                  events: {
                    mouseOut: () => tooltipRef.current?.hide(),
                    mouseOver(this: HoveredPoint) {
                      if (chart.isEmpty) {
                        return;
                      }
                      const { container, plotLeft, plotTop } = this.series.chart;
                      const { left, top } = container.getBoundingClientRect();
                      tooltipRef.current?.show({
                        index: this.index,
                        x: left + plotLeft + this.plotX,
                        y: top + plotTop + this.plotY
                      });
                    }
                  }
                }
              },
              plotMarkersEnabled: false,
              series: [
                {
                  data: chart.data,
                  name: chart.seriesName
                }
              ],
              // Highcharts draws its tooltip inside the chart container, where the lists clip it
              tooltipConfig: { enabled: false },
              tooltipHoverEnabled: !chart.isEmpty,
              type: 'area',
              xAxisConfig: {
                categories: chart.categories,
                crosshair: { color: HOVER_GUIDE_COLOR, width: 1, zIndex: 3 },
                visible: false
              },
              yAxisConfigs: [
                {
                  visible: false,
                  ...(chart.isEmpty
                    ? { softMax: 1, softMin: 0 }
                    : { min: getYAxisFloor(chart.data ?? []), startOnTick: false })
                }
              ]
            }}
          />
          <span className="quickReportChart__graphFooter">{chart.legend}</span>
          <QuickReportTooltipPortal getProps={index => getTooltipProps(chart.tooltipPoints[index])} ref={tooltipRef} />
        </div>
      </div>
    </div>
  );
}
